import { TranslationKey } from '@/modules/i18n/types'
import type { DriverHook, Popover } from 'driver.js'

const minimizeClickHandlers = new WeakMap<Element, EventListener>()

const nextOnElementClick: DriverHook = (element, _step, { driver }) => {
  if (!element) {
    return
  }

  const handler: EventListener = () => {
    window.requestAnimationFrame(() => {
      if (driver.hasNextStep()) {
        driver.moveNext()
      } else {
        driver.destroy()
      }
    })
  }

  minimizeClickHandlers.set(element, handler)
  element.addEventListener('click', handler, { once: true })
}

const unsubscribeElement: DriverHook = (element) => {
  if (!element) {
    return
  }

  const handler = minimizeClickHandlers.get(element)

  if (handler) {
    element.removeEventListener('click', handler)
    minimizeClickHandlers.delete(element)
  }
}

type WorkspaceTourStep = {
  element: string
  title: TranslationKey
  description: TranslationKey
  disableActiveInteraction?: boolean
  side?: "top" | "right" | "bottom" | "left"
  popover?: Omit<Popover, 'title' | 'description'>
  onHighlighted?: DriverHook
  onDeselected?: DriverHook
}

export const getWorkspaceTourSteps = (assignmentTour: boolean): WorkspaceTourStep[] => {
  return [
    {
      element: '[data-tour-id="task-name"]',
      title: 'workspaceTour.taskName.title',
      description: 'workspaceTour.taskName.description'
    },
    {
      element: '[data-tour-id="assignment-pane"]',
      title: 'workspaceTour.assignment.title',
      description: 'workspaceTour.assignment.description',
      disableActiveInteraction: true
    },
    {
      element: '[data-tour-id="assignment-pane"] [data-tour-id="minimize-pane"]',
      title: 'workspaceTour.minimize.title',
      description: 'workspaceTour.minimize.description',
      popover: {
        showButtons: []
      },
      onHighlighted: nextOnElementClick,
      onDeselected: unsubscribeElement
    },
    {
      element: '[data-tour-id="assignment-pane"]',
      title: 'workspaceTour.itWentHere.title',
      description: 'workspaceTour.itWentHere.description',
      popover: {
        showButtons: []
      },
      onHighlighted: nextOnElementClick,
      onDeselected: unsubscribeElement
    },
    {
      element: '[data-tour-id="solution-pane"]',
      title: 'workspaceTour.solution.title',
      description: assignmentTour ? 'workspaceTour.solution.assignmentDescription' : 'workspaceTour.solution.description',
      disableActiveInteraction: true
    },
    {
      element: '[data-tour-id="queryHistory-pane"]',
      title: 'workspaceTour.queryHistory.title',
      description: 'workspaceTour.queryHistory.description',
      disableActiveInteraction: true
    },
    {
      element: '[data-tour-id="schema-pane"]',
      title: 'workspaceTour.schema.title',
      description: 'workspaceTour.schema.description',
      disableActiveInteraction: true
    },
    {
      element: '[data-tour-id="query-editor"]',
      title: 'workspaceTour.queryEditor.title',
      description: 'workspaceTour.queryEditor.description',
      disableActiveInteraction: true
    },
    {
      element: '[data-tour-id="query-results"]',
      title: 'workspaceTour.queryResults.title',
      description: 'workspaceTour.queryResults.description'
    },
    {
      element: '[data-tour-id="submit-solution"]',
      title: 'workspaceTour.submitSolution.title',
      description: 'workspaceTour.submitSolution.description',
      popover: {
        doneBtnText: "Let's go!"
      },
      disableActiveInteraction: true
    }
  ]
}

